import React from "react";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import { NavigatorMenuItem } from "../../CommandHub/NavigatorDetailsTooltipTitle";
import RswNavigator from "../../../interfaces/robot/navigator";
import Collapse from "@mui/material/Collapse";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import { useTranslation } from "react-i18next";
import OptionSwitchInput from "../../Inputs/OptionSwitchInput";
import Typography from "@mui/material/Typography";
import { useSharedSelector } from "../../../store/SharedProvider";
import { selectRobotRswSoftwareVersion } from "../../../store/robot-configurations/robot-configurations-selectors";
import { isVersionAGreaterThanB } from "../../../utils/version-util";
import { selectIsWebApp } from "../../../store/app-configurations/app-configurations-selectors";

export interface NavigatorChangeProps {
  navigatorName: string;
  doWallFollow: boolean;
  repeats: number;
}

const NavigatorSelection: React.FC<{
  navigatorName: string;
  navigators: RswNavigator[];
  onChange: (payload: Partial<NavigatorChangeProps>) => void;
  doWallFollow?: boolean;
  repeats?: number;
  hideNavigators?: boolean;
  getLabelForNavigator?: (navigatorName: string) => string;
  /** Render only the mode-picker cards, only the wall-follow/repeat config, or (default) both. */
  variant?: "cards" | "config";
}> = ({
  navigatorName,
  doWallFollow,
  navigators,
  repeats,
  hideNavigators,
  onChange,
  getLabelForNavigator,
  variant,
}) => {
  const { t } = useTranslation();

  const rswSoftwareVersion = useSharedSelector((state) => selectRobotRswSoftwareVersion(state));
  const isWebApp = useSharedSelector((state) => selectIsWebApp(state));

  const onNavigatorSelect = (newNavigator: string) => {
    const newNavigatorConfig = navigators.find(nav => nav.name === newNavigator);
    onChange({ navigatorName: newNavigator, repeats: newNavigatorConfig?.isRepeatConfigurable ? undefined : 1, doWallFollow: newNavigatorConfig?.isWallFollowConfigurable ? true : false });
  };

  const toggleWallFollowHandler = () => {
    onChange({ doWallFollow: !doWallFollow });
  }

  const repeatsChangeHandler = (newValue: number) => {
    onChange({ repeats: newValue });
  }

  const navigator = navigators.find(nav => nav.name === navigatorName);

  const repeatOptions = [
    { value: 1, label: t("label-mission-repeat-once") },
    { value: 2, label: t("label-mission-repeat-twice") },
    { value: 3, label: t("label-mission-repeat-thrice") },
  ];

  if (isWebApp || (rswSoftwareVersion && !isVersionAGreaterThanB("2026.1.0", rswSoftwareVersion))) {
    repeatOptions.push({ value: -1, label: t("label-mission-repeat-continuously") });
  }

  const cards = (
    <Stack direction="row" flexWrap="wrap">
      {navigators
        .filter((nav) => !nav.hideInList)
        .map((nav) => (
          <NavigatorMenuItem
            key={nav.name}
            navigator={nav}
            onClick={onNavigatorSelect}
            selected={navigatorName === nav.name}
            label={getLabelForNavigator?.(nav.name)}
          />
        ))}
      {navigators.length % 2 === 1 && <Box sx={{ flex: "1 0 50%" }} />}
    </Stack>
  );

  if (variant === "cards") return cards;

  return (
    <React.Fragment>
      <Grid item xs={12}>
        <Stack spacing={1}>
          {variant !== "config" && (
            <Collapse in={hideNavigators === true ? false : navigator?.hideInList !== true}>
              <Box sx={(theme) => ({ width: "100%", overflow: "auto", maxHeight: theme.spacing(47) })}>
                {/* 47 height means the start of next row is shown to indicate scrollable content available */}
                {cards}
              </Box>
            </Collapse>
          )}
          <Collapse in={hideNavigators === true ? false : navigator?.isWallFollowConfigurable && doWallFollow != null}>
            {doWallFollow != null && (
              <FormControlLabel
                control={<Switch checked={doWallFollow} onChange={toggleWallFollowHandler} />}
                label={<Typography variant="subtitle2">{t("input-label-do-wall-follow")}</Typography>}
              />
            )}
          </Collapse>
          <Collapse in={hideNavigators === true ? false : navigator?.isRepeatConfigurable && repeats != null}>
            {repeats != null && (
              <Grid>
                <Typography variant="subtitle2">{t("mission-repeat-label")}</Typography>
                <OptionSwitchInput value={repeats} options={repeatOptions} onChange={repeatsChangeHandler} />
              </Grid>
            )}
          </Collapse>
        </Stack>
      </Grid>
    </React.Fragment>
  );
};

export default NavigatorSelection;
